import { useMemo, type CSSProperties } from 'react';
import { cx } from './index';

// The code on the product, in the maker's brand. Shared by the homepage (Variant C), sector pages and
// product pages. Decorative: these codes are not scannable. Every maker, model and serial is fictional.

// ---------- Seeded random, so each serial gets its own (decorative, not scannable) code ----------
function rng(seed: string) {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

// ---------- A QR in the maker's brand: dot modules, rounded eyes, their monogram in the middle ----------
export function BrandedQr({
  accent,
  mark,
  size = 120,
  dots = true,
  seed,
  ink = '#FFFFFF',
  bg,
  className,
}: {
  accent: string;
  mark: string;
  size?: number;
  dots?: boolean;
  seed?: string;
  ink?: string; // monogram colour on the accent badge
  bg?: string; // quiet zone colour; omit for transparent (etched on metal)
  className?: string;
}) {
  const N = 25;
  const cells = useMemo(() => {
    const r = rng(seed ?? `${mark}-${accent}`);
    const out: [number, number][] = [];
    const inFinder = (x: number, y: number) => (x < 8 && y < 8) || (x >= N - 8 && y < 8) || (x < 8 && y >= N - 8);
    const inMark = (x: number, y: number) => mark !== '' && x >= 9 && x <= 15 && y >= 9 && y <= 15;
    for (let y = 0; y < N; y++)
      for (let x = 0; x < N; x++) {
        if (inFinder(x, y) || inMark(x, y)) continue;
        if (r() > 0.5) out.push([x, y]);
      }
    return out;
  }, [seed, mark, accent]);
  const eyes: [number, number][] = [
    [0, 0],
    [N - 7, 0],
    [0, N - 7],
  ];
  return (
    <svg width={size} height={size} viewBox="-1 -1 27 27" className={className} aria-hidden="true">
      {bg && <rect x="-1" y="-1" width="27" height="27" rx="2" fill={bg} />}
      <g fill={accent}>
        {cells.map(([x, y]) =>
          dots ? <circle key={`${x}-${y}`} cx={x + 0.5} cy={y + 0.5} r={0.42} /> : <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />
        )}
      </g>
      {eyes.map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect x={x + 0.5} y={y + 0.5} width="6" height="6" rx={dots ? 2 : 0} fill="none" stroke={accent} strokeWidth="1" />
          <rect x={x + 2} y={y + 2} width="3" height="3" rx={dots ? 1 : 0} fill={accent} />
        </g>
      ))}
      {mark && (
        <g>
          <rect x="9.6" y="9.6" width="5.8" height="5.8" rx="1.6" fill={accent} />
          <text x="12.5" y="12.62" textAnchor="middle" dominantBaseline="central" fontFamily="Poppins, sans-serif" fontWeight="800" fontSize="4.1" fill={ink}>
            {mark}
          </text>
        </g>
      )}
    </svg>
  );
}

// ---------- The maker's plate or label, with their code made part of it ----------
export type PlateFinish = 'steel' | 'fleet' | 'label' | 'slip' | 'anodised';

export type PlateSpec = {
  finish: PlateFinish;
  maker: string;
  kind: string;
  mark: string; // monogram in the middle of the code
  accent: string; // maker's colour
  qrInk?: string; // module colour if different from accent (e.g. etched on dark metal)
  monoInk?: string; // monogram letter colour
  rows: [string, string][];
  serialKey: string; // which row is the serial (shown in mono)
  meet: string; // the product introducing itself
};

export function MakerPlate({ p, className }: { p: PlateSpec; className?: string }) {
  const metal = p.finish === 'steel' || p.finish === 'fleet' || p.finish === 'anodised';
  const serial = p.rows.find(([k]) => k === p.serialKey)?.[1] ?? p.maker;
  return (
    <div className={cx('mp', `mp-${p.finish}`, className)} style={{ '--mp-accent': p.accent } as CSSProperties}>
      <style>{MAKER_PLATE_CSS}</style>
      {metal && (
        <>
          <i className="mp-rivet" style={{ left: 9, top: 9 }} />
          <i className="mp-rivet" style={{ right: 9, top: 9 }} />
          <i className="mp-rivet" style={{ left: 9, bottom: 9 }} />
          <i className="mp-rivet" style={{ right: 9, bottom: 9 }} />
        </>
      )}
      <div className="mp-top">
        <span className="mp-maker">{p.maker}</span>
        <span className="mp-kind">{p.kind}</span>
      </div>
      <div className="mp-body">
        <div className="mp-rows">
          {p.rows.map(([k, v]) => (
            <div key={k} className={cx('mp-row', k === p.serialKey && 'is-serial')}>
              <span>{k}</span>
              <b className={k === p.serialKey ? 'font-mono' : undefined}>{v}</b>
            </div>
          ))}
        </div>
        <div className="mp-code">
          <div className="mp-qr">
            <BrandedQr accent={p.qrInk ?? p.accent} mark={p.mark} seed={serial} ink={p.monoInk ?? '#FFFFFF'} size={96} className="mp-qr-svg" />
          </div>
          <div className="mp-meet">{p.meet}</div>
        </div>
      </div>
    </div>
  );
}

const MAKER_PLATE_CSS = `
/* ---------- the maker's plate ---------- */
.v2 .mp { position: relative; color: var(--mp-ink); border-radius: 10px; padding: 18px 22px 16px; background: var(--mp-bg); box-shadow: var(--mp-shadow); --mp-shadow: 0 30px 60px -24px rgba(0,0,0,.7); }
.v2 .mp-rivet { position: absolute; width: 9px; height: 9px; border-radius: 50%; background: radial-gradient(circle at 35% 35%, #F4F5F7, #B9BEC7 60%, #9AA0AA); box-shadow: 0 1px 0 rgba(255,255,255,.6), inset 0 0 0 .5px rgba(0,0,0,.25); }
.v2 .mp-top { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 0 6px 9px; border-bottom: 1.5px solid var(--mp-rule); }
.v2 .mp-maker { font-weight: 800; font-size: 20px; letter-spacing: .1em; text-transform: uppercase; color: var(--mp-accent); }
.v2 .mp-kind { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; font-weight: 600; color: var(--mp-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.v2 .mp-body { display: grid; grid-template-columns: minmax(0, 1fr) auto; }
.v2 .mp-rows { padding: 4px 18px 0 6px; }
.v2 .mp-row { display: flex; justify-content: space-between; align-items: baseline; gap: 14px; padding: 6px 0; border-bottom: 1px solid var(--mp-rule2); }
.v2 .mp-row:last-child { border-bottom: 0; }
.v2 .mp-row span { font-size: 9.5px; letter-spacing: .12em; text-transform: uppercase; color: var(--mp-muted); font-weight: 600; white-space: nowrap; }
.v2 .mp-row b { font-size: 13px; font-weight: 600; text-align: right; }
.v2 .mp-row.is-serial b { font-size: 13.5px; font-weight: 700; }
.v2 .mp-code { display: grid; justify-items: center; align-content: center; gap: 8px; padding: 12px 2px 0 20px; border-left: 1.5px solid var(--mp-rule); }
.v2 .mp-qr { padding: 8px; border-radius: 10px; background: var(--mp-qrbg); box-shadow: var(--mp-qrsh); line-height: 0; }
.v2 .mp-meet { font-size: 11.5px; font-weight: 700; color: var(--mp-accent); white-space: nowrap; }

/* engraved steel */
.v2 .mp-steel, .v2 .mp-fleet { --mp-ink: #2A2D35; --mp-muted: #646975; --mp-rule: rgba(42,45,53,.4); --mp-rule2: rgba(42,45,53,.15); --mp-qrbg: rgba(0,0,0,.035); --mp-qrsh: inset 0 1px 2px rgba(0,0,0,.18), 0 1px 0 rgba(255,255,255,.7);
  --mp-bg: repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, rgba(0,0,0,.025) 1px 2px, rgba(255,255,255,0) 2px 3px), linear-gradient(118deg, #F1F2F4 0%, #D8DBE0 38%, #EDEEF1 56%, #CDD1D7 100%);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.12), inset 0 1.5px 0 rgba(255,255,255,.85), 0 30px 60px -24px rgba(0,0,0,.7); }
.v2 .mp-steel .mp-maker, .v2 .mp-steel .mp-row b, .v2 .mp-steel .mp-meet { text-shadow: 0 1px 0 rgba(255,255,255,.75); }

/* hire-fleet plate: aluminium with the maker's safety-yellow band */
.v2 .mp-fleet { padding-top: 0; }
.v2 .mp-fleet .mp-top { margin: 0 -22px 4px; padding: 12px 28px 10px; background: #F2C230; border-radius: 10px 10px 0 0; border-bottom: 1.5px solid #1B1B1B; }
.v2 .mp-fleet .mp-kind { color: #1B1B1B; opacity: .75; }
.v2 .mp-fleet .mp-rivet:nth-of-type(1), .v2 .mp-fleet .mp-rivet:nth-of-type(2) { background: radial-gradient(circle at 35% 35%, #fff3c4, #c99d1c 70%); }

/* printed rating label */
.v2 .mp-label { --mp-ink: #22251F; --mp-muted: #6C7068; --mp-rule: rgba(34,37,31,.25); --mp-rule2: rgba(34,37,31,.12); --mp-qrbg: #fff; --mp-qrsh: 0 0 0 1px rgba(34,37,31,.1);
  --mp-bg: #FBFAF6; border-radius: 8px; padding-top: 0; box-shadow: 0 0 0 1px rgba(0,0,0,.06), 0 30px 60px -24px rgba(0,0,0,.65); }
.v2 .mp-label .mp-top { margin: 0 -22px 6px; padding: 12px 28px 11px; background: var(--mp-accent); border-radius: 8px 8px 0 0; border-bottom: 0; }
.v2 .mp-label .mp-maker { color: #fff; letter-spacing: .02em; text-transform: none; font-size: 22px; }
.v2 .mp-label .mp-kind { color: rgba(255,255,255,.78); }

/* shipping label on the packing slip */
.v2 .mp-slip { --mp-ink: #1B1D22; --mp-muted: #6A6E78; --mp-rule: #1B1D22; --mp-rule2: rgba(27,29,34,.14); --mp-qrbg: #fff; --mp-qrsh: none;
  --mp-bg: #fff; border-radius: 4px; box-shadow: 0 0 0 1px rgba(0,0,0,.08), 0 30px 60px -24px rgba(0,0,0,.6); }
.v2 .mp-slip::before { content: ''; position: absolute; left: 10px; right: 10px; top: 6px; border-top: 1.5px dashed rgba(27,29,34,.22); }
.v2 .mp-slip .mp-top { padding-top: 8px; }
.v2 .mp-slip .mp-maker { letter-spacing: .04em; text-transform: none; font-size: 21px; }
.v2 .mp-slip .mp-row b { font-family: 'Geist Mono', ui-monospace, monospace; font-weight: 500; font-size: 12.5px; }

/* dark anodised rear panel, engraved */
.v2 .mp-anodised { --mp-ink: #D5D6DC; --mp-muted: #8E909A; --mp-rule: rgba(255,255,255,.2); --mp-rule2: rgba(255,255,255,.09); --mp-qrbg: rgba(255,255,255,.03); --mp-qrsh: inset 0 1px 2px rgba(0,0,0,.6), 0 1px 0 rgba(255,255,255,.08);
  --mp-bg: repeating-linear-gradient(90deg, rgba(255,255,255,.025) 0 1px, rgba(0,0,0,.05) 1px 2px, rgba(255,255,255,0) 2px 3px), linear-gradient(118deg, #2C2D33 0%, #1C1D21 42%, #2B2C31 64%, #1A1B1F 100%);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.07), inset 0 1px 0 rgba(255,255,255,.12), 0 30px 60px -24px rgba(0,0,0,.8); }
.v2 .mp-anodised .mp-rivet { background: radial-gradient(circle at 35% 35%, #6b6d74, #2b2c31 70%); }
.v2 .mp-anodised .mp-maker { letter-spacing: .22em; font-weight: 700; }

@media (max-width: 640px) {
  .v2 .mp { padding: 14px 14px 12px; }
  .v2 .mp-fleet, .v2 .mp-label { padding-top: 0; }
  .v2 .mp-fleet .mp-top, .v2 .mp-label .mp-top { margin: 0 -14px 4px; padding: 10px 18px 9px; }
  .v2 .mp-maker { font-size: 16px; }
  .v2 .mp-label .mp-maker { font-size: 18px; }
  .v2 .mp-kind { display: none; }
  .v2 .mp-rows { padding: 2px 12px 0 2px; }
  .v2 .mp-row { flex-direction: column; align-items: flex-start; gap: 0; padding: 4px 0; }
  .v2 .mp-row span { font-size: 8.5px; }
  .v2 .mp-row b { font-size: 12px; text-align: left; }
  .v2 .mp-code { padding: 8px 0 0 12px; }
  .v2 .mp-qr-svg { width: 84px; height: 84px; }
  .v2 .mp-meet { font-size: 10px; white-space: normal; text-align: center; max-width: 104px; }
}
`;
